import { createPortal } from 'react-dom';
import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import EFAmazon from 'elem/ui-assets/icons/unregular/EFAmazon';
import EfImg from 'elem/ui/ef-img';
import EfLink from 'elem/ui/ef-link';
import EfSpec from 'elem/ui/ef-spec';
import EfTitle from 'elem/ui/ef-title';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useProductStore } from '../../../ProductProvider';
import { imgLocaleA, imgLocaleB, imgLocaleC } from '../B/Imgs';
import { EFBattery, EFCompact, EFExpand, EFRuntime, EFUPS } from '../B';
import Dialog from '../B/Dialog';
import {
    US_Product_List,
    CA_Product_List,
    US_Product_Options,
    CA_Product_Options,
    BR_Product_List,
    BR_Product_Options,
    MX_Product_List,
    MX_Product_Options,
    PH_Product_List,
    PH_Product_Options,
} from './common';
import styles from './index.module.scss';
import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';
import { getSolarRecommand } from '@/service/solar-calculator';

/**
 * ps：好乱，有点改不动🥲，把 B 复制了一份缩减后特供给 NA
 * includes: [us, ca, br, mx, ph] 2025-07-17
 */
export default function TheComp() {
    const { t } = useLang('theProduct.OtherA');
    const { locale = 'us', query } = useRouter();
    const { pageStatus } = useProductStore();
    const [active, setActive] = useState(0);

    const timer = query._timer ? new Date(query._timer as string) : new Date();

    const getTimeValue = useCallback(
        (date: any, value: any) => {
            if (!date) return value; // 如果没有传入日期，则直接返回value
            const startTime = new Date(date?.startTime);
            const endTime = new Date(date?.endTime ?? '9999/09/09');
            if (timer < startTime) return null; // 未开始
            if (timer > startTime && timer < endTime) return value; // 进行中
            if (timer > endTime) return null; // 已结束
        },
        [timer],
    );

    const ProductList = useMemo(() => {
        switch (locale) {
            case 'us':
                return US_Product_List;
            case 'ca':
                return CA_Product_List;
            case 'br':
                return BR_Product_List;
            case 'mx':
                return MX_Product_List;
            case 'ph':
                return PH_Product_List;
            default:
                return US_Product_List;
        }
    }, [locale]);

    const ProductOptions = useMemo(() => {
        switch (locale) {
            case 'us':
                return US_Product_Options;
            case 'ca':
                return CA_Product_Options;
            case 'br':
                return BR_Product_Options;
            case 'mx':
                return MX_Product_Options;
            case 'ph':
                return PH_Product_Options;
            default:
                return US_Product_Options;
        }
    }, [locale]);

    const List = [
        {
            pc: imgLocaleA?.[locale]?.pc ?? imgLocaleA['us']?.pc,
            title: t('item1_title'),
            desc: t('item1_desc'),
            link_more: ProductList[0]?.link_more.replace('https://us.ecoflow.com', `https://${locale}.ecoflow.com`),
            link_view: ProductList[0]?.link_view,
            icons:
                locale === 'ph'
                    ? [
                          {
                              icon: 'EFCompact',
                              title: t('item1_text1'),
                          },
                          {
                              icon: 'EFRuntime',
                              title: t('item1_text2'),
                          },
                          {
                              icon: 'EFBattery',
                              title: t('item1_text3'),
                          },
                          {
                              icon: 'EFUPS',
                              title: t('item1_text4'),
                          },
                      ]
                    : [
                          {
                              icon: 'EFUPS',
                              title: t('item1_text1'),
                          },
                          {
                              icon: 'EFRuntime',
                              title: t.rich('item1_text2', {
                                  sup: (v: string) => <sup>{v}</sup>,
                              }),
                          },
                          {
                              icon: 'EFCompact',
                              title: t('item1_text3'),
                          },
                      ],
        },
        {
            pc: imgLocaleB?.[locale]?.pc ?? imgLocaleB['us']?.pc,
            title: t('item2_title'),
            desc: t('item2_desc'),
            link_more: ProductList[1]?.link_more.replace('https://us.ecoflow.com', `https://${locale}.ecoflow.com`),
            link_view: ProductList[1]?.link_view,
            icons:
                locale === 'ph'
                    ? [
                          {
                              icon: 'EFRuntime',
                              title: t('item2_text1'),
                          },
                          {
                              icon: 'EFExpand',
                              title: t('item2_text2'),
                          },
                          {
                              icon: 'EFBattery',
                              title: t('item2_text3'),
                          },
                          {
                              icon: 'EFUPS',
                              title: t('item2_text4'),
                          },
                      ]
                    : [
                          {
                              icon: 'EFUPS',
                              title: t('item2_text1'),
                          },
                          {
                              icon: 'EFRuntime',
                              title: t.rich('item2_text2', {
                                  sup: (v: string) => <sup>{v}</sup>,
                              }),
                          },
                          {
                              icon: 'EFExpand',
                              title: t('item2_text3'),
                          },
                      ],
        },
        {
            pc: imgLocaleC?.[locale]?.pc ?? imgLocaleC['us']?.pc,
            title: t('item3_title'),
            desc: t('item3_desc'),
            link_more: ProductList[2]?.link_more.replace('https://us.ecoflow.com', `https://${locale}.ecoflow.com`),
            link_view: ProductList[2]?.link_view,
            needBest: locale === 'ph',
            icons:
                locale === 'ph'
                    ? [
                          {
                              icon: 'EFRuntime',
                              title: t('item3_text1'),
                          },
                          {
                              icon: 'EFExpand',
                              title: t('item3_text2'),
                          },
                          {
                              icon: 'EFBattery',
                              title: t('item3_text3'),
                          },
                          {
                              icon: 'EFUPS',
                              title: t('item3_text4'),
                          },
                      ]
                    : [
                          {
                              icon: 'EFUPS',
                              title: t('item3_text1'),
                          },
                          {
                              icon: 'EFRuntime',
                              title: t.rich('item3_text2', {
                                  sup: (v: string) => <sup>{v}</sup>,
                              }),
                          },
                          {
                              icon: 'EFExpand',
                              title: t('item3_text3'),
                          },
                      ],
        },
    ].map((item, idx) => {
        const data = {
            ...item,
            ...ProductOptions[idx],
        };
        if (locale === 'ca' && idx === 1) {
            const a = getTimeValue(
                {
                    startTime: '2025/01/09 16:00:00 GMT+8',
                },
                '1',
            );
            if (a === '1') return data;
            return {
                ...item,
                isDialog: true,
                label: 'Coming soon',
            };
        }
        return data;
    });

    const [items, setItems] = useState(List);

    const handleChangeTab = (k: number) => {
        setActive(k);
    };

    // 获取价格信息
    useEffect(() => {
        if (pageStatus !== 'statusA') {
            (async () => {
                const getTemp = async (item: any, idx: number) => {
                    const { productId, variantId } = item;
                    const res = await getSolarRecommand({ productId, variantId }, locale);
                    const temp = { ...items[idx] } as any;
                    if (res?.code === '0') {
                        temp.discountPrice = res?.data?.price;
                        temp.originalPrice = res?.data?.compareAtPrice || res?.data?.price;
                        temp.inventory = res?.data?.inventoryQuantity ?? undefined;
                    }
                    return temp;
                };
                if (Array.isArray(ProductList)) {
                    const promises = ProductList.map(getTemp);
                    Promise.all(promises).then((res) => {
                        setItems([...res, ...items.slice(res.length)]);
                    });
                } else {
                    getTemp(ProductList, 0).then((res) => {
                        setItems([res, ...items.slice(1)]);
                    });
                }
            })();
        }
    }, []);

    const [showDialog, setShowDialog] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const handleOpenDialog = (i: any) => {
        setShowDialog(true);
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_join',
            button_name: t('btn3'),
        });
    };
    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsClient(true);
        }
    }, []);
    const dateString = useMemo(() => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);
    const fieldsMap = (locale: string) => {
        const maps = {
            ca: {
                contactListId: ['1831281292', '1157235157'],
                fields: [
                    {
                        id: '11150',
                        type: 'multiple',
                        value: [2],
                        overwrite: false,
                    },
                    {
                        id: '4424',
                        type: 'single',
                        value: 1,
                        overwrite: true,
                    },
                    {
                        id: '11149',
                        type: 'text',
                        value: dateString,
                        overwrite: true,
                    },
                ],
            },
        };
        return maps[locale] || [];
    };
    const configs = {
        needTerms: true,
        isMulti: true,
        termsHtml: (() => {
            const maps = {
                us: "I consent to the <a href='https://us.ecoflow.com/policies/terms-of-service' target='_blank'>Terms of Use</a> and <a href='https://account.ecoflow.com/agreement/en-us/PrivacyPolicy.html' target='_blank'>Privacy Policy</a>.",
                ca: "I consent to the <a href='https://ca.ecoflow.com/policies/terms-of-service' target='_blank'>Terms of Use</a> and <a href='https://account.ecoflow.com/agreement/en-ca/PrivacyPolicy.html' target='_blank'>Privacy Policy</a>.",
                br: `Eu concordo com os <a href="https://account.ecoflow.com/agreement/pt-br/TermsOfUse.html" target="_blank">Termos de Uso</a> e a <a href="https://account.ecoflow.com/agreement/pt-br/PrivacyPolicy.html" target="_blank">Política de Privacidade</a>.`,
                mx: "Doy mi consentimiento a los <a href='https://account.ecoflow.com/agreement/es-mx/TermsOfUse.html' target='_blank'>Términos de uso</a> y a la <a href='https://account.ecoflow.com/agreement/es-mx/PrivacyPolicy.html' target='_blank'>Política de privacidad</a>.",
            };
            return maps[locale] || maps.us;
        })(),
        ...fieldsMap(locale),
        tipsObj: {
            successTips: t('successTips'),
        },
    };
    const productList = [];
    if (locale === 'ca') {
        const startTime = new Date('2025/01/09 16:00:00 GMT+8');
        if (startTime >= timer) {
            productList.push({
                value: 6,
                label: t('win_product1'),
                checked: true,
                fields: [
                    {
                        id: '11150',
                        type: 'multiple',
                        value: 6,
                        overwrite: false,
                    },
                    {
                        id: '9435',
                        type: 'multiple',
                        value: 15,
                        overwrite: false,
                    },
                ],
            });
        }
    }
    productList.push({
        value: 7,
        label: t('win_product2'),
        checked: true,
        fields: [
            {
                id: '11150',
                type: 'multiple',
                value: 7,
                overwrite: false,
            },
            {
                id: '9435',
                type: 'multiple',
                value: 15,
                overwrite: false,
            },
        ],
    });
    return (
        <div className={styles.TheComp} id="id-OtherA">
            <EfSpec className={'spec-x'} />
            <EfTitle hasAnimation={false}>{t('title')}</EfTitle>
            <ul className="items">
                {items?.map((item: any, k: number) => (
                    <li className={`item${active === k ? ' active' : ''}`} key={k}>
                        {/* 右上角 */}
                        {item.needBenefit &&
                            getTimeValue(
                                item.benefitTimes,
                                <div className="item-top-right">
                                    <div className="item-top-right-title">30%</div>
                                    <div className="item-top-right-subtitle">off</div>
                                    <div className="item-top-right-add">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M5.88888 0.555542H4.1111V4.11091L0.555557 4.11091V5.88869H4.1111V9.44443H5.88888V5.88869H9.44445V4.11091L5.88888 4.11091V0.555542Z"
                                                fill="white"
                                            />
                                        </svg>
                                    </div>
                                    <EfImg className="item-top-right-img" pc="https://websiteoss.ecoflow.com/upload/image/1733126749635/sl_sp.png" />
                                    <div className="item-top-right-desc">Free 45W portable solar panel</div>
                                </div>,
                            )}
                        {item.needBest && <div className="item-top-right-best">{t('best')}</div>}
                        <div className="item-top">
                            <div className="item-img">
                                <EfImg pc={item?.pc} />
                            </div>
                            <p className="item-title">{item.title}</p>
                            <p className="item-desc">{item.desc}</p>
                            <ul className="item-lists">
                                {item?.icons?.map((list: any, m: number) => (
                                    <li className="item-list" key={`${k}_${m}`}>
                                        <div className="list-icon">
                                            {list.icon === 'EFUPS' ? (
                                                <EFUPS id={`${k}_${m}`} />
                                            ) : list.icon === 'EFRuntime' ? (
                                                <EFRuntime id={`${k}_${m}`} />
                                            ) : list.icon === 'EFBattery' ? (
                                                <EFBattery id={`${k}_${m}`} />
                                            ) : list.icon === 'EFExpand' ? (
                                                <EFExpand id={`${k}_${m}`} />
                                            ) : (
                                                <EFCompact id={`${k}_${m}`} />
                                            )}
                                        </div>
                                        <p className="list-text">{list.title}</p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="item-footer">
                            {item?.label ? (
                                <p className="item-label">{item?.label}</p>
                            ) : (
                                item?.discountPrice && (
                                    <div className="item-price-box">
                                        <span className="item-price price-discount">{FormatMoneyLocal(item?.discountPrice, locale)}</span>
                                        {item?.discountPrice !== item?.originalPrice && (
                                            <span className="item-price price-original">{FormatMoneyLocal(item?.originalPrice, locale)}</span>
                                        )}
                                    </div>
                                )
                            )}
                            <div className={`item-btns`}>
                                {item?.isAmazon && (
                                    <>
                                        {item?.link_view &&
                                            getTimeValue(
                                                item.amazonTimes,
                                                <EfLink
                                                    className="btn amazon"
                                                    href={item.link_view}
                                                    dataLayer={{
                                                        event: 'uaEvent',
                                                        event_name: 'buy_on_amazon',
                                                        product_name: item.title,
                                                        button_name: t('btn1'),
                                                        list_name: t('title'),
                                                    }}
                                                >
                                                    <span className="buy">{t('btn1')}</span>
                                                    <EFAmazon />
                                                </EfLink>,
                                            )}
                                        <EfLink
                                            className={`btn shopify ${item?.disable ? 'disable' : ''}`}
                                            href={item.link_more}
                                            dataLayer={{
                                                event: 'uaEvent',
                                                event_name: 'buy',
                                                product_name: item.title,
                                                position: `In-Products Module`,
                                            }}
                                        >
                                            {t('btn2')}
                                        </EfLink>
                                    </>
                                )}
                                {item?.isDialog && (
                                    <button className="btn notify" onClick={() => handleOpenDialog(item)}>
                                        {t('btn3')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
            <ul className="tabs">
                {items?.map((item: any, k: number) => (
                    <li className={`tab${active === k ? ' active' : ''}`} key={k} onClick={() => handleChangeTab(k)}>
                        {item.title}
                    </li>
                ))}
            </ul>
            <EfSpec className={'spec-x'} />
            {showDialog && isClient && createPortal(<Dialog configs={{ ...configs, productList }} callback={() => setShowDialog(false)} />, document.body)}
        </div>
    );
}
